/**
 * Target languages that carry the Atlas course track.
 * German and Swiss German stay on their existing sections.
 * English is included here as a course route only.
 */
import activeCourseTargets from '../../data/json/active-course-targets.json' with { type: 'json' };
import { courseLocaleFileComplete } from './course-interface-readiness.js';
import { readCourseAudioStatuses } from './course-audio-status.js';

export const COURSE_TARGET_LANGS = Object.freeze([
  "bg",
  "es",
  "cs",
  "da",
  "de",
  "et",
  "el",
  "en",
  "fr",
  "ga",
  "hr",
  "it",
  "lv",
  "lt",
  "hu",
  "mt",
  "nl",
  "pl",
  "pt",
  "ro",
  "sk",
  "sl",
  "fi",
  "sv",
  "hi",
  "hy",
  "th",
  "ko",
  "sq", "ar", "zh", "ka", "id", "ja", "ne", "fa", "ru", "sw", "tr", "vi",
]);

/** English names. Course chrome stays English until the structure settles. */
export const COURSE_LANGUAGE_NAMES = Object.freeze({
  bg: "Bulgarian",
  es: "Spanish",
  cs: "Czech",
  da: "Danish",
  de: "German",
  et: "Estonian",
  el: "Greek",
  en: "English",
  fr: "French",
  ga: "Irish",
  hr: "Croatian",
  it: "Italian",
  lv: "Latvian",
  lt: "Lithuanian",
  hu: "Hungarian",
  mt: "Maltese",
  nl: "Dutch",
  pl: "Polish",
  pt: "Portuguese",
  ro: "Romanian",
  sk: "Slovak",
  sl: "Slovenian",
  fi: "Finnish",
  sv: "Swedish",
  hi: "Hindi",
  hy: "Armenian",
  th: "Thai",
  sq: "Albanian",
  ar: "Arabic",
  zh: "Chinese",
  ka: "Georgian",
  id: "Indonesian",
  ja: "Japanese",
  ko: "Korean",
  ne: "Nepali",
  fa: "Persian",
  ru: "Russian",
  sw: "Swahili",
  tr: "Turkish",
  vi: "Vietnamese",
});

/** English course slugs. Each interface language overrides these from its course-slugs locale file. */
export const COURSE_TARGET_SLUGS = Object.freeze({
  bg: "bulgarian",
  es: "spanish",
  cs: "czech",
  da: "danish",
  et: "estonian",
  el: "greek",
  fr: "french",
  ga: "irish",
  hr: "croatian",
  it: "italian",
  lv: "latvian",
  lt: "lithuanian",
  hu: "hungarian",
  mt: "maltese",
  nl: "dutch",
  pl: "polish",
  pt: "portuguese",
  ro: "romanian",
  sk: "slovak",
  sl: "slovenian",
  fi: "finnish",
  sv: "swedish",
  hi: "hindi",
  hy: "armenian",
  th: "thai",
  sq: "albanian",
  ar: "arabic",
  zh: "chinese",
  ka: "georgian",
  id: "indonesian",
  ja: "japanese",
  ko: "korean",
  ne: "nepali",
  fa: "persian",
  ru: "russian",
  sw: "swahili",
  tr: "turkish",
  vi: "vietnamese",
});

export const COURSE_TARGET_LANDING = Object.freeze(
  Object.fromEntries(Object.entries(COURSE_TARGET_SLUGS).map(([code, slug]) => [code, `learn-${slug}`])),
);

const COURSE_TARGET_SET = new Set(COURSE_TARGET_LANGS);
const PUBLIC_TARGET_LANGS = Object.freeze(["de", "ch", "es"]);
const PUBLIC_COURSE_TARGETS = new Set();

/**
 * Courses that have been built, in homepage order.
 * The public homepage and learn menu use {@link visibleCourseTargetLangs}.
 */
export const ACTIVE_COURSE_TARGET_LANGS = Object.freeze([...activeCourseTargets]);

export function isCourseTarget(code) {
  return COURSE_TARGET_SET.has(String(code || "").trim().toLowerCase());
}

export function courseLanguageName(code, uiLang = "en") {
  const c = String(code || "").trim().toLowerCase();
  if (uiLang === "de" && COURSE_LANGUAGE_NAMES[c]) return new Intl.DisplayNames(["de"], { type: "language" }).of(c);
  return COURSE_LANGUAGE_NAMES[c] || "";
}

/**
 * Production builds list only courses whose audio is complete.
 * Dev servers list every built course, including ones still missing audio.
 */
export function courseListingIsFiltered() {
  const nodeEnv = process.env["NODE_ENV"];
  const filtered = nodeEnv === "production";
  return filtered;
}

/** New courses are part of the site. Which ones are linked is {@link courseIsPublic}. */
export function showNewTargetLangs() {
  return true;
}

/** The recording inventory reports every required clip on disk. */
export function courseAudioIsComplete(code) {
  const row = readCourseAudioStatuses()[String(code || "").trim().toLowerCase()];
  return row?.status === "complete";
}

/**
 * A built course is listed, linked, and indexable when dev is showing everything
 * or a production build has its full audio inventory. Other built courses stay
 * on direct URLs.
 */
export function courseIsPublic(code) {
  const c = String(code || "").trim().toLowerCase();
  if (!isCourseTarget(c) || !ACTIVE_COURSE_TARGET_LANGS.includes(c)) return false;
  if (!courseListingIsFiltered()) return true;
  return courseAudioIsComplete(c);
}

/** Homepage buttons and, with Swiss German, the learn menu. */
export function visibleCourseTargetLangs() {
  return Object.freeze(ACTIVE_COURSE_TARGET_LANGS.filter((code) => courseIsPublic(code)));
}

/** Learn menu: German and Swiss German, plus every course this server lists. */
export function getNavTargetLangs() {
  const codes = ["de", "ch"];
  for (const code of visibleCourseTargetLangs()) {
    if (!codes.includes(code)) codes.push(code);
  }
  return Object.freeze(codes);
}

/** Target languages that have a survival guide, and the English page for each. */
const SURVIVAL_GUIDE_PATHS = Object.freeze({ mt: "/en/maltese/survival-phrases" });
export const SURVIVAL_GUIDE_TARGETS = Object.freeze(Object.keys(SURVIVAL_GUIDE_PATHS));

export function survivalGuidePath(code) {
  return SURVIVAL_GUIDE_PATHS[String(code || "").trim().toLowerCase()] || "";
}

/**
 * Dev links every built guide. Production links one only when
 * LL_SURVIVAL_GUIDE_PUBLIC=1. Until then the pages answer 404.
 */
export function survivalGuideIsPublic(code) {
  if (!survivalGuidePath(code)) return false;
  if (!courseListingIsFiltered()) return true;
  return process.env.LL_SURVIVAL_GUIDE_PUBLIC === "1";
}

export function publicSurvivalGuideTargets() {
  return Object.freeze(SURVIVAL_GUIDE_TARGETS.filter((code) => survivalGuideIsPublic(code)));
}

export function firstConversationIsPublic() {
  return survivalGuideIsPublic("mt");
}

/** Language landings for an unfinished course stay out of search. German, Swiss German, and Spanish story landings stay public. */
export function coursePagesAreHidden(targetCode) {
  const code = String(targetCode || "").trim().toLowerCase();
  if (PUBLIC_TARGET_LANGS.includes(code)) return false;
  if (!isCourseTarget(code)) return false;
  return !courseIsPublic(code);
}

/** Course sections stay off menus, out of the sitemap, and noindex until the course is public. Direct URLs still render. */
export function courseSectionIsUnlisted(targetCode) {
  const code = String(targetCode || "").trim().toLowerCase();
  if (PUBLIC_COURSE_TARGETS.has(code)) return false;
  if (!isCourseTarget(code)) return false;
  return !courseIsPublic(code);
}

/** Finished courses can be explained in German once that locale file covers the English catalog. */
const GERMAN_PHASE_TARGETS = COURSE_TARGET_LANGS;
const EN_ONLY = Object.freeze(["en"]), EN_DE = Object.freeze(["en", "de"]);
export const COURSE_INTERFACE_LANGS = Object.freeze(Object.defineProperties({}, Object.fromEntries(
  GERMAN_PHASE_TARGETS.map(target => [target, { enumerable: true, get: () => courseLocaleFileComplete(target, "de") ? EN_DE : EN_ONLY }]),
)));
export const courseInterfaceLanguages = target => COURSE_INTERFACE_LANGS[target] || EN_ONLY;
export const courseInterfaceLanguage = (ui, target) => courseInterfaceLanguages(target).includes(ui) ? ui : "en";

/** Plain map for the browser nav. The client must not read locale files to choose a course URL. */
export function courseInterfaceLangMap() {
  return Object.fromEntries(COURSE_TARGET_LANGS.map(code => [code, courseInterfaceLanguages(code)]));
}

/** Course buttons name the English fallback until the selected interface has a complete locale file. */
export function courseButtonLabel(label, uiLang, targetCode) {
  const text = String(label || "");
  const ui = String(uiLang || "").trim().toLowerCase();
  if (!text || courseInterfaceLanguage(ui, targetCode) === ui) return text;
  return text.endsWith("(in English)") ? text : `${text} (in English)`;
}
